import { AssetTypeItemComponentBase } from './asset-type-item.component.base';
import { Component, ElementRef, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { CrudEditorParentService, CrudEditorSelfService, positiveIntegerValidator, requiredIfValidator, requiredValidator, UserStorage } from '@scisys-uim/uim-common';
import { RefCode, RequirementRule } from 'assets-model';
import { AssetTypeService, RefCodeStorage } from 'assets-service';
import { RichTextSectionComponent } from '../../textsection/rich-text-sections/rich-text-section.component';


@Component({
  selector: 'app-asset-type-item',
  templateUrl: './asset-type-item.component.html',
  styleUrls: ['./asset-type-item.component.scss'],
  providers: [CrudEditorParentService],
  viewProviders: [CrudEditorSelfService]
})
export class AssetTypeItemComponent extends AssetTypeItemComponentBase {

  @ViewChild('guidanceTextEditor') guidanceTextEditor: RichTextSectionComponent;

  ragStatusSelectionList: RefCode[];

  constructor(protected assetTypeService: AssetTypeService,
    protected refCodeStorage: RefCodeStorage,
    protected fb: FormBuilder,
    protected elementRef: ElementRef,
    protected router: Router,
    protected route: ActivatedRoute,
    protected userStorage: UserStorage,
    protected dialog: MatDialog,
    protected injector: Injector,
    protected snackbar: MatSnackBar) {
    super(assetTypeService, refCodeStorage, fb, elementRef, router, route, userStorage, dialog, injector, snackbar);
  }

  protected onInitAsyncComplete() {
    super.onInitAsyncComplete();
    this.maintenanceTypeLoadSelectionList();
    this.ragStatusLoadSelectionList();
    this.electricalAspectRequirementLoadSelectionList();
    this.mechanicalAspectRequirementLoadSelectionList();
    this.maintenanceAspectRequirementLoadSelectionList();
  }

  createForm(): FormGroup {
    return this.fb.group({
      assetTypeId: [null, []],
      comments: [null, []],
      createdBy: [null, []],
      createdDate: [null, []],
      electricalAspectRequirement: [null, [requiredValidator,]],
      guidanceText: [null, []],
      havs: [null, [requiredValidator,]],
      havsRagStatus: [null, []],
      lockVersion: [null, []],
      maintenanceAspectRequirement: [null, [requiredValidator,]],
      maintenanceType: [null, []],
      mechanicalAspectRequirement: [null, [requiredValidator,]],
      name: [null, [requiredValidator,]],
      periodicMaintenance: [null, [requiredValidator,]],
      periodicMaintenanceDays: [null, [positiveIntegerValidator]],
      portableApplianceTestingDays: [null, [positiveIntegerValidator]],
      routineMaintenance: [null, [requiredValidator,]],
      routineMaintenanceDays: [null, [positiveIntegerValidator]],
      transferOnApproval: [null, [requiredValidator,]],
      updatedBy: [null, []],
      updatedDate: [null, []],
      visualInspection: [null, [requiredValidator,]],
      wbv: [null, [requiredValidator,]],
      wbvRagStatus: [null, []]
    }, {
      validators: [
        requiredIfValidator(() => this.portableApplianceTestingDays, () => this.item?.electricalAspectRequirement == 'REQUIRED', 'Portable Appliance Testing Days', { required: 'Portable Appliance Testing Days is required when an Electrical Aspect is required' }),
        requiredIfValidator(() => this.maintenanceType, () => this.item?.maintenanceAspectRequirement == 'REQUIRED', 'Maintenance Type', { required: 'Maintenance Type is required when a Maintenance Aspect is required' }),
      ]
    });
  }

  itemSet() {
    super.itemSet();
    if (this.guidanceTextEditor) {
      this.guidanceTextEditor.readOnly = true;
    }
  }

  ragStatusLoadSelectionList() {
    this.refCodeStorage.getDomainElements('RAG_STATUS').subscribe(
      data => {
        this.ragStatusSelectionList = data.sort((a, b) => (a.val1Num - b.val1Num));
      }
    );
  }


  setDefaultDisabledFields(): void {
    super.setDefaultDisabledFields();
    this.createdBy?.disable();
    this.createdDate?.disable();
    this.updatedBy?.disable();
    this.updatedDate?.disable();
    this.assetTypeId.disable();

    this.enableDisableElectricalFields();
    this.enableDisableMechanicalFields();
    this.enableDisableMaintenanceFields();

    this.periodicMaintenanceDays?.disable();
    if (this.item.periodicMaintenance) {
      this.periodicMaintenanceDays?.enable();
    }

    this.routineMaintenanceDays?.disable();
    if (this.item.routineMaintenance) {
      this.routineMaintenanceDays?.enable();
    }
  }

  enableDisableElectricalFields(enable: boolean = this.item?.electricalAspectRequirement != 'NOT_ALLOWED') {
    if (enable) {
      this.portableApplianceTestingDays.enable();
    } else {
      this.portableApplianceTestingDays.disable();
    }
  }

  enableDisableMechanicalFields(enable: boolean = this.item?.mechanicalAspectRequirement != 'NOT_ALLOWED') {
    if (enable) {
      this.havs.enable();
      this.havsRagStatus?.disable();
      if (this.item.havs) {
        this.havsRagStatus?.enable();
      }
      this.wbv.enable();
      this.wbvRagStatus?.disable();
      if (this.item.wbv) {
        this.wbvRagStatus?.enable();
      }
    } else {
      this.havs.disable();
      this.havsRagStatus.disable();
      this.wbv.disable();
      this.wbvRagStatus.disable();
    }
  }

  enableDisableMaintenanceFields(enable: boolean = this.item?.maintenanceAspectRequirement != 'NOT_ALLOWED') {
    if (enable) {
      this.maintenanceType.enable();
      this.visualInspection.enable();
      this.periodicMaintenance.enable();
      this.periodicMaintenanceDays?.disable();
      if (this.item.periodicMaintenance) {
        this.periodicMaintenanceDays?.enable();
      }
      this.routineMaintenance.enable();
      this.routineMaintenanceDays?.disable();
      if (this.item.routineMaintenance) {
        this.routineMaintenanceDays?.enable();
      }
    } else {
      this.maintenanceType.disable();
      this.visualInspection.disable();
      this.periodicMaintenance.disable();
      this.periodicMaintenanceDays.disable();
      this.routineMaintenance.disable();
      this.routineMaintenanceDays.disable();
    }
  }

  havsChangedByUser(value: boolean, unsetDependents?: boolean): void {
    super.havsChangedByUser(value, unsetDependents);
    if (value) {
      this.havsRagStatus.enable();
    } else {
      this.havsRagStatus.disable();
      this.item.havsRagStatus = null;
    }
  }

  wbvChangedByUser(value: boolean, unsetDependents?: boolean): void {
    super.wbvChangedByUser(value, unsetDependents);
    if (value) {
      this.wbvRagStatus.enable();
    } else {
      this.wbvRagStatus.disable();
      this.item.wbvRagStatus = null;
    }
  }

  periodicMaintenanceChangedByUser(value: boolean, unsetDependents?: boolean): void {
    super.periodicMaintenanceChangedByUser(value, unsetDependents);
    if (value) {
      this.periodicMaintenanceDays.enable();
    } else {
      this.periodicMaintenanceDays.disable();
      this.item.periodicMaintenanceDays = null;
    }
  }

  routineMaintenanceChangedByUser(value: boolean, unsetDependents?: boolean): void {
    super.routineMaintenanceChangedByUser(value, unsetDependents);
    if (value) {
      this.routineMaintenanceDays.enable();
    } else {
      this.routineMaintenanceDays.disable();
      this.item.routineMaintenanceDays = null;
    }
  }

  maintenanceAspectRequirementChangedByUser(value: RequirementRule, unsetDependents?: boolean): void {
    super.maintenanceAspectRequirementChangedByUser(value, unsetDependents);
    this.enableDisableMaintenanceFields(value != 'NOT_ALLOWED');
    if (!value) {
      this.item.visualInspection = false;
      this.item.maintenanceType = null;
      this.item.periodicMaintenance = false;
      this.item.periodicMaintenanceDays = null;
      this.item.routineMaintenance = false;
      this.item.routineMaintenanceDays = null;
    }
  }

  mechanicalAspectRequirementChangedByUser(value: RequirementRule, unsetDependents?: boolean): void {
    super.mechanicalAspectRequirementChangedByUser(value, unsetDependents);
    this.enableDisableMechanicalFields(value != 'NOT_ALLOWED');
    if (!value) {
      this.item.havs = false;
      this.item.havsRagStatus = null;
      this.item.wbv = false;
      this.item.wbvRagStatus = null;
    }
  }

  electricalAspectRequirementChangedByUser(value: RequirementRule, unsetDependents?: boolean): void {
    super.electricalAspectRequirementChangedByUser(value, unsetDependents);
    this.enableDisableElectricalFields(value != 'NOT_ALLOWED');
    if (!value) {
      this.item.portableApplianceTestingDays = null;
    }
  }

}





